"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import * as Icon from "@phosphor-icons/react/dist/ssr";
import { useTranslations } from "next-intl";
import { Link, useRouter } from "@/navigation";
import useMenuMobile from "@/store/useMenuMobile";
import { menus } from "@/lib/menu";
import GetInstantQuoteButton from "@/components/Contact/GetInstantQuote";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";
import { CloudUploadOutlined, DownOutlined, RightOutlined } from "@ant-design/icons";
import { useCategory } from "@/context/CategoriesContext";
import { filterCateImg } from "@/components/Home2/Collection";
import { getBlogCls } from "@/lib/api/blog";
import { Blog } from "@/lib/@types/api/blog";
import { defaultLocale } from "@/config";
import CategoriesMenuTwo from "@/components/Header/Menu/CategoriesMenuTwo.tsx";

interface MaterialCategory {
	id: number;
	name: string;
	link?: string;
	children?: MaterialCategory[];
}

const getMaterialsNav = (t: any): MaterialCategory[] => [
	{
		id: 1,
		name: "materials.CNC machining materials",
		children: [
			{
				id: 11,
				name: "materials.All CNC metals",
				link: "/materials/allcncmetals",
				children: [
					{ id: 101, name: "materials.Titanium", link: "/materials/allcncmetals/titanium" },
					{ id: 102, name: "materials.Steel", link: "/materials/allcncmetals/steel" },
					{ id: 103, name: "materials.Stainless Steel", link: "/materials/allcncmetals/stainless-steel" },
					{ id: 104, name: "materials.Zinc Alloy", link: "/materials/allcncmetals/zinc-alloy" },
					{
						id: 105,
						name: "materials.Chromium-Nickel Alloy",
						link: "/materials/allcncmetals/chromium-nickel-alloy",
					},
					{ id: 106, name: "materials.Brass", link: "/materials/allcncmetals/brass" },
					{ id: 107, name: "materials.Copper", link: "/materials/allcncmetals/copper" },
					{ id: 108, name: "materials.Aluminum", link: "/materials/allcncmetals/aluminum" },
					{ id: 109, name: "materials.Bronze", link: "/materials/allcncmetals/bronze" },
					{ id: 110, name: "materials.Cold Rolled Steel", link: "/materials/allcncmetals/cold-rolled-steel" },
				],
			},
			{
				id: 12,
				name: "materials.All CNC plastics",
				link: "/materials/allcncplastics",
				children: [
					{ id: 111, name: "materials.Polycarbonate (PC)", link: "/materials/allcncplastics/pc" },
					{ id: 112, name: "materials.Polyoxymethylene (POM)", link: "/materials/allcncplastics/pom" },
					{ id: 113, name: "materials.Polyphthalamide (PPA)", link: "/materials/allcncplastics/ppa" },
					{ id: 114, name: "materials.Polyphenylene Sulfide (PPS)", link: "/materials/allcncplastics/pps" },
					{ id: 115, name: "materials.Teflon (PTFE)", link: "/materials/allcncplastics/ptfe" },
					{ id: 116, name: "materials.Polyvinyl Chloride (PVC)", link: "/materials/allcncplastics/pvc" },
					{
						id: 117,
						name: "materials.Ultra High Molecular WeightPolyethylene (UPE)",
						link: "/materials/allcncplastics/upe",
					},
					{
						id: 118,
						name: "materials.Polyether Ether Ketone (PEEK)",
						link: "/materials/allcncplastics/peek",
					},
					{ id: 119, name: "materials.Nylon (PA)", link: "/materials/allcncplastics/pa" },
					{
						id: 120,
						name: "materials.Glass-Filled Nylon",
						link: "/materials/allcncplastics/glass-filled-nylon",
					},
				],
			},
		],
	},
	{
		id: 2,
		name: "materials.3D Printing Materials",
		children: [
			{
				id: 21,
				name: "materials.All 3D Printing Plastics",
				children: [
					{ id: 201, name: "materials.PLA" },
					{ id: 202, name: "materials.ABS" },
					{ id: 203, name: "materials.PETG" },
					{ id: 204, name: "materials.Nylon" },
					{ id: 205, name: "materials.ASA" },
					{ id: 206, name: "materials.PEI" },
					{ id: 207, name: "materials.PETG" },
					{ id: 208, name: "materials.PC" },
					{ id: 209, name: "materials.Resin" },
				],
			},
		],
	},
	{
		id: 3,
		name: "materials.Injection Molding Materials",
		children: [
			{
				id: 31,
				name: "materials.All Injection Molding Plastics",
				children: [
					{ id: 301, name: "materials.ABS" },
					{ id: 302, name: "materials.PP" },
					{ id: 303, name: "materials.PE" },
					{ id: 304, name: "materials.PS" },
					{ id: 305, name: "materials.PC" },
					{ id: 306, name: "materials.PA" },
				],
			},
		],
	},
	{
		id: 4,
		name: "materials.Sheet Metal Fabrication Materials",
		children: [
			{
				id: 41,
				name: "materials.All Sheet Metals",
				children: [
					{ id: 401, name: "materials.Cold rolled steel plate (SPCC)" },
					{ id: 402, name: "materials.Stainless steel (304)" },
					{ id: 403, name: "materials.Aluminum plate (5052)" },
					{ id: 404, name: "materials.Hot dip galvanized sheet (SGCC)" },
					{ id: 405, name: "materials.Copper" },
					{ id: 406, name: "materials.Hot rolled steel plate (SPHC)" },
				],
			},
		],
	},
	{
		id: 5,
		name: "materials.Metal Casting Materials",
		children: [
			{
				id: 51,
				name: "materials.All Casting Metal",
				children: [
					{ id: 501, name: "materials.Gray Cast Iron" },
					{ id: 502, name: "materials.Ductile Iron" },
					{ id: 503, name: "materials.Cast Steel" },
					{ id: 504, name: "materials.Cast Aluminum Alloy" },
					{ id: 505, name: "materials.Cast Copper Alloy" },
					{ id: 506, name: "materials.Cast Zinc Alloy" },
				],
			},
		],
	},
];

const MenuTwo = ({ locale }: { locale: string }) => {
	const router = useRouter();

	const t = useTranslations();
	const materialsNav = getMaterialsNav(t);
	const { openModalInquiry } = useModalInquiryContext();
	const { openMenuMobile, handleMenuMobile } = useMenuMobile();
	const [fixedHeader, setFixedHeader] = useState(false);
	const [lastScrollPosition, setLastScrollPosition] = useState(0);
	const { categories } = useCategory();
	const [blogCls, setBlogCls] = useState<Blog.BlogCl[]>([]);
	const [showService, setShowService] = useState(false);
	const [showChildrenID, setShowChildrenID] = useState(-1);
	const [showGrandChildIndex, setShowGrandChildIndex] = useState(-1);
	useEffect(() => {
		getBlogCls({ lang_code: { lang_code: locale } }).then((res) => {
			if (res) {
				setBlogCls(res);
			}
		});
	}, []);
	useEffect(() => {
		const handleScroll = () => {
			const scrollPosition = window.scrollY;
			setFixedHeader(scrollPosition > 0 && scrollPosition < lastScrollPosition);
			setLastScrollPosition(scrollPosition);
		};
		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [lastScrollPosition]);
	const setMenuShow = (id: number) => {
		if (id === 2) {
			setShowService(!showService);
		} else {
			if (showChildrenID === id) {
				setShowChildrenID(-1);
			} else {
				setShowChildrenID(id);
			}
		}
	};
	const setgs = (index: number) => {
		if (showGrandChildIndex === index) {
			setShowGrandChildIndex(-1);
		} else {
			setShowGrandChildIndex(index);
		}
	};
	const GoToPage = (url: string) => {
		router.push(url);
		handleMenuMobile();
	};

	return (
		<>
			<header className={`header-menu style-one !fixed top-[40px] h-[65px] w-full bg-white`}>
				<div className="container mx-auto h-full">
					<div className="header-main flex h-full items-center justify-between gap-2">
						<div className="menu-mobile-icon flex shrink-0 items-center lg:hidden" onClick={handleMenuMobile}>
							<i className="icon-category text-2xl"></i>
						</div>
						{/* 中间品牌区：移动端占满剩余宽度，避免 Logo 被 flex 挤压裁切 */}
						<div className="flex min-w-0 flex-1 items-center md:flex-none">
							<Link href={"/"} className="shrink-0 !text-black">
								{/* 手机/平板：40×40；lg 及以上 PC：原设计 100×50 */}
								<div className="relative flex h-10 w-10 shrink-0 items-center justify-center lg:h-[50px] lg:w-[100px]">
									<Image
										src="/image/logo1.png"
										width={100}
										height={50}
										alt="JS Precision logo"
										className="h-full w-full object-contain"
										priority
									/>
								</div>
							</Link>
							<Link href={"/"} className="min-w-0 !text-black">
								<h2 className="ml-2 min-w-0 truncate text-base font-bold md:ml-6 md:overflow-visible md:text-clip md:whitespace-normal md:text-xl">
									JS Precision
								</h2>
							</Link>
						</div>
						<nav className="menu-main h-full max-lg:hidden">
							<ul className="flex h-full items-center gap-10">
								{menus.map((item) => {
									{
										return (
											item.show && (
												<li
													className={`group h-full ${
														item.id === 4 || item.id === 9 || item.id === 15 ? "relative" : ""
													}`}
													key={item.id}
												>
													{!item.hasSlug && item.id !== 15 ? (
														<div className="text-button-uppercase flex h-full items-center justify-center text-black duration-300">
															{t(item.name)}
														</div>
													) : (
														<Link
															href={item.link}
															className="text-button-uppercase flex h-full items-center justify-center text-black duration-300"
														>
															{t(item.name)}
														</Link>
													)}
													{item.children && (
														<div className="sub-menu absolute -left-0 max-h-[500px] overflow-y-auto rounded-b-sm bg-gray-50">
															<ul>
																{item.children.map((child, index) => {
																	return (
																		<li
																			key={child.id}
																			className="w-full cursor-pointer text-nowrap px-3 py-2"
																		>
																			<Link
																				href={child.link}
																				className="block font-bold text-black hover:!text-main"
																			>
																				{t(child.name)}
																			</Link>
																		</li>
																	);
																})}
															</ul>
														</div>
													)}
													{/* {
												item.id===15 &&
												<CategoriesMenuFive link={item.link} name={t(item.name)} children={item.children} locale={locale}></CategoriesMenuFive>
											} */}
													{item.id === 2 && (
														// mega-menu
														<CategoriesMenuTwo locale={locale}></CategoriesMenuTwo>
													)}
													{item.id === 3 && (
														// mega-menu
														<div className="mega-menu absolute left-0 top-[74px] grid max-h-[500px] w-screen overflow-y-auto rounded-b-sm bg-gray-50 px-3 py-5">
															<ul className="col-span-2 grid h-fit grid-cols-5 gap-2">
																{blogCls.map((item) => {
																	return (
																		<li
																			key={item.cls_id}
																			className="group relative h-fit rounded-lg border border-gray-200 bg-white p-2 shadow-sm transition-all duration-700 hover:bg-[#004ead29] hover:shadow-main"
																		>
																			<Link
																				href={"/category/" + item.cls_slug}
																				className="flex items-center justify-between text-black"
																			>
																				<div className="flex items-center gap-x-2">
																					<Image
																						src={item.banner_img_url || "/image/default-image.webp"}
																						width={30}
																						quality={50}
																						height={30}
																						alt={item.cls_name}
																						className="rounded object-cover"
																					/>
																					<div className="font-bold">{item.cls_name}</div>
																				</div>
																			</Link>
																		</li>
																	);
																})}
															</ul>
															<div className="grid grid-cols-2 gap-x-2"></div>
														</div>
													)}
													{item.id === 30 && (
														<div className="absolute left-0 right-0 top-[101%] mx-auto w-fit origin-top scale-y-0 transform rounded-b-sm bg-white px-3 py-5 transition-all duration-500 ease-in-out group-hover:visible group-hover:scale-y-100 group-hover:opacity-100">
															<div className="mx-auto grid w-[1200px] grid-cols-[4fr_3fr_3fr] gap-6 bg-[#fff] p-10">
																{/* item1 占 40% */}
																<div className="flex flex-col gap-2 border-r border-gray-300 pr-6">
																	<div className="text-lg font-extrabold text-black">
																		{t(materialsNav[0].name)}
																	</div>
																	<div className="grid grid-cols-2 gap-4">
																		{materialsNav[0].children?.map((subCategory) => (
																			<div key={subCategory.id} className="flex flex-col gap-2">
																				{subCategory.link ? (
																					<Link
																						href={subCategory.link}
																						className="font-semibold text-black hover:text-main"
																					>
																						{t(subCategory.name)}
																					</Link>
																				) : (
																					<div className="font-semibold text-black">
																						{t(subCategory.name)}
																					</div>
																				)}
																				<ul className="flex flex-col gap-y-2">
																					{subCategory.children?.map((item) => (
																						<li
																							key={item.id}
																							className="cursor-pointer text-black hover:text-main"
																						>
																							{item.link ? (
																								<Link href={item.link} className="text-black hover:text-main">
																									{t(item.name)}
																								</Link>
																							) : (
																								<span>{t(item.name)}</span>
																							)}
																						</li>
																					))}
																				</ul>
																			</div>
																		))}
																	</div>
																</div>
																{/* item2 和 item3 占 30% */}
																<div className="grid grid-rows-2 gap-6 border-r border-gray-300 pr-6">
																	{materialsNav.slice(1, 3).map((materialCategory) => (
																		<div key={materialCategory.id} className="flex flex-col gap-2">
																			<div className="text-lg font-extrabold text-black">
																				{t(materialCategory.name)}
																			</div>
																			{materialCategory.children?.map((subCategory) => (
																				<div key={subCategory.id} className="flex flex-col gap-2">
																					{subCategory.link ? (
																						<Link
																							href={subCategory.link}
																							className="font-semibold text-black hover:text-main"
																						>
																							{t(subCategory.name)}
																						</Link>
																					) : (
																						<div className="font-semibold text-black">
																							{t(subCategory.name)}
																						</div>
																					)}
																					<ul className="flex flex-col gap-y-2">
																						{subCategory.children?.map((item) => (
																							<li
																								key={item.id}
																								className="cursor-pointer text-black hover:text-main"
																							>
																								{item.link ? (
																									<Link
																										href={item.link}
																										className="text-black hover:text-main"
																									>
																										{t(item.name)}
																									</Link>
																								) : (
																									<span>{t(item.name)}</span>
																								)}
																							</li>
																						))}
																					</ul>
																				</div>
																			))}
																		</div>
																	))}
																</div>
																{/* item4 和 item5 占 30% */}
																<div className="grid grid-rows-2 gap-6">
																	{materialsNav.slice(3, 5).map((materialCategory) => (
																		<div key={materialCategory.id} className="flex flex-col gap-2">
																			<div className="text-lg font-extrabold text-black">
																				{t(materialCategory.name)}
																			</div>
																			{materialCategory.children?.map((subCategory) => (
																				<div key={subCategory.id} className="flex flex-col gap-2">
																					{subCategory.link ? (
																						<Link
																							href={subCategory.link}
																							className="font-semibold text-black hover:text-main"
																						>
																							{t(subCategory.name)}
																						</Link>
																					) : (
																						<div className="font-semibold text-black">
																							{t(subCategory.name)}
																						</div>
																					)}
																					<ul className="flex flex-col gap-y-2">
																						{subCategory.children?.map((item) => (
																							<li
																								key={item.id}
																								className="cursor-pointer text-black hover:text-main"
																							>
																								{item.link ? (
																									<Link
																										href={item.link}
																										className="text-black hover:text-main"
																									>
																										{t(item.name)}
																									</Link>
																								) : (
																									<span>{t(item.name)}</span>
																								)}
																							</li>
																						))}
																					</ul>
																				</div>
																			))}
																		</div>
																	))}
																</div>
															</div>
														</div>
													)}
												</li>
											)
										);
									}
								})}
							</ul>
						</nav>
						<div className="right flex shrink-0 items-center gap-12">
							<GetInstantQuoteButton className="max-md:hidden" />
							<div className="hidden max-md:block">
								<Link href="/upload">
									<CloudUploadOutlined />
								</Link>
							</div>
						</div>
					</div>
				</div>
			</header>
			<div id="menu-mobile" className={` ${openMenuMobile ? "open" : ""} `}>
				<div className="menu-container h-full bg-white">
					<div className="container h-full">
						<div className="menu-main h-full overflow-auto">
							<div className="heading relative flex items-center justify-center py-2">
								<div
									className="close-menu-mobile-btn bg-surface absolute left-0 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full"
									onClick={handleMenuMobile}
								>
									<Icon.X size={14} />
								</div>
								<div
									onClick={() => GoToPage("/")}
									className="logo text-button-uppercase text-center text-3xl font-semibold text-black"
								>
									JS Precision
								</div>
							</div>
							<div className="form-search relative mt-2">
								<Icon.MagnifyingGlass
									size={20}
									className="absolute left-3 top-1/2 -translate-y-1/2 cursor-pointer"
								/>
								<input
									type="text"
									placeholder="What are you looking for?"
									className="border-line h-12 w-full rounded-lg border pl-10 pr-4 text-sm"
								/>
							</div>
							<div className="list-nav mt-6">
								<ul>
									{menus.map((item) => {
										{
											return (
												item.show && (
													<li className="mb-5 h-full" key={item.id}>
														{!item.hasSlug ? (
															<div
																className="text-button-uppercase flex justify-between !rounded-lg border p-2 text-black duration-300"
																onClick={() => setMenuShow(item.id)}
															>
																<span>{t(item.name)}</span>
																<RightOutlined />
															</div>
														) : (
															<div
																onClick={() => GoToPage(item.link)}
																className="text-button-uppercase flex justify-between !rounded-lg border p-2 text-black duration-300"
															>
																<span>{t(item.name)}</span>
																<RightOutlined />
															</div>
														)}

														{item.id === 2 && showService && (
															<div className="transform pt-2 transition-all duration-500">
																{categories?.edges.map((item, index) => {
																	return (
																		<div key={item.node.id} className="my-1 rounded-lg pr-2 text-black">
																			<div className="mb-1 flex items-center justify-between gap-x-2 rounded-md border border-gray-300 pr-2">
																				<div
																					className="flex w-fit items-center px-1"
																					onClick={() => GoToPage(`/${item.node.slug}`)}
																				>
																					<Image
																						src={
																							filterCateImg(item.node.metadata)[0]?.url ||
																							"/image/default-image.webp"
																						}
																						width={50}
																						quality={50}
																						height={50}
																						alt={item.node.name}
																						className="h-[40px] w-[40px] rounded object-cover"
																					/>
																					<div className="font-bold">
																						{locale === defaultLocale
																							? item.node.name
																							: item.node.translation.name || item.node.name}
																					</div>
																				</div>
																				<div
																					className="h-6 w-6 rounded-full bg-[#4296ec] text-center !text-white"
																					onClick={() => setgs(index)}
																				>
																					{showGrandChildIndex === index ? (
																						<DownOutlined />
																					) : (
																						<RightOutlined />
																					)}
																				</div>
																			</div>

																			{showGrandChildIndex === index && (
																				<div className={`ml-2 rounded-md border border-gray-300`}>
																					{item.node.children &&
																						item.node.children.edges.map((child) => {
																							return (
																								<div
																									key={child.node.slug}
																									className="p-1"
																									onClick={() => GoToPage(`/${child.node.slug}`)}
																								>
																									<div className="flex items-center p-1">
																										<Image
																											src={
																												filterCateImg(child.node.metadata)[0]?.url ||
																												"/image/default-image.webp"
																											}
																											width={50}
																											quality={50}
																											height={50}
																											alt={child.node.name}
																											className="rounded object-cover px-1"
																										/>
																										<div className="font-bold">
																											{locale === defaultLocale
																												? child.node.name
																												: child.node.translation.name || child.node.name}
																										</div>
																									</div>
																								</div>
																							);
																						})}
																				</div>
																			)}
																		</div>
																	);
																})}
															</div>
														)}
														{item.id === showChildrenID && item.children && (
															<div className="transform pt-2 transition-all duration-500">
																<ul>
																	{item.children.map((child, index) => {
																		return (
																			<li key={index} className="w-full cursor-pointer text-nowrap px-3 py-2">
																				<div
																					onClick={() => GoToPage(child.link)}
																					className="block font-bold text-black hover:!text-main"
																				>
																					{t(child.name)}
																				</div>
																			</li>
																		);
																	})}
																</ul>
															</div>
														)}
													</li>
												)
											);
										}
									})}
								</ul>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default MenuTwo;
